/**
 * Availability change highlighting — the single source of truth for how
 * Added / Updated / Deleted availability slots are colored before a PM approves.
 *
 * Imported by BOTH the PM view (ModeratorWeeklyAvailabilityCard, used from
 * TeamAvailability) and the moderator's own page (ModeratorAvailability) so the
 * highlighting is guaranteed identical in both. Nothing here is
 * gated by role.
 *
 * Pure functions only (no React, no I/O) so they are cheap to unit-test to 100%.
 */

export type SlotChangeKind = 'added' | 'updated' | 'deleted' | 'unchanged';

/** Minimal slot shape used for diffing (wall-clock "HH:MM" start/end). */
export interface HighlightSlot {
  start: string;
  end: string;
}

export interface DiffSlot extends HighlightSlot {
  kind: SlotChangeKind;
  /** For 'updated' / 'deleted', the baseline value being changed. */
  previous?: HighlightSlot;
}

/**
 * Tailwind class fragments per change kind. Used for the slot row container in
 * both views. Extends the existing amber(pending)/emerald(approved) palette so
 * the whole surface reads as one system.
 */
export const CHANGE_STYLES: Record<SlotChangeKind, string> = {
  added: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  updated: 'border-amber-300 bg-amber-50 text-amber-900',
  deleted: 'border-red-300 bg-red-50 text-red-900 line-through opacity-70',
  unchanged: 'border-border bg-background text-foreground',
};

/** Badge label per (non-unchanged) change kind. */
export const CHANGE_LABEL: Record<Exclude<SlotChangeKind, 'unchanged'>, string> = {
  added: 'Added',
  updated: 'Updated',
  deleted: 'Deleted',
};

/** Badge class per (non-unchanged) change kind (border/bg/text only). */
export const CHANGE_BADGE_STYLES: Record<Exclude<SlotChangeKind, 'unchanged'>, string> = {
  added: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  updated: 'border-amber-200 bg-amber-50 text-amber-900',
  deleted: 'border-red-200 bg-red-50 text-red-900',
};

/** Tailwind classes for a change kind (empty string for 'unchanged' callers can ignore). */
export function changeKindClass(kind: SlotChangeKind): string {
  return CHANGE_STYLES[kind] ?? CHANGE_STYLES.unchanged;
}

const normalizeHm = (value: string | null | undefined): string => {
  const s = String(value ?? '').trim();
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return s;
  return `${m[1].padStart(2, '0')}:${m[2]}`;
};

const toKey = (slot: HighlightSlot): string =>
  `${normalizeHm(slot.start)}-${normalizeHm(slot.end)}`;

/**
 * Diff one day's current slots against its baseline (previously saved/approved)
 * slots and classify each as added / updated / deleted / unchanged.
 *
 * Matching rule: a slot is matched to a baseline slot by START time.
 *  - same start, same end  → unchanged
 *  - same start, diff end   → updated (carries `previous`)
 *  - start not in baseline  → added
 *  - baseline start not in current → deleted "ghost" row (carries `previous`)
 *
 * Result is sorted by start time so deleted ghosts slot in beside their peers.
 */
export function diffDaySlots(
  current: ReadonlyArray<HighlightSlot>,
  baseline: ReadonlyArray<HighlightSlot>,
): DiffSlot[] {
  const baseByStart = new Map<string, HighlightSlot>();
  for (const b of baseline) {
    const start = normalizeHm(b.start);
    if (!baseByStart.has(start)) {
      baseByStart.set(start, { start, end: normalizeHm(b.end) });
    }
  }

  const out: DiffSlot[] = [];
  const matched = new Set<string>();

  for (const c of current) {
    const start = normalizeHm(c.start);
    const end = normalizeHm(c.end);
    const base = baseByStart.get(start);
    if (!base) {
      out.push({ start, end, kind: 'added' });
      continue;
    }
    matched.add(start);
    if (base.end === end) {
      out.push({ start, end, kind: 'unchanged' });
    } else {
      out.push({ start, end, kind: 'updated', previous: base });
    }
  }

  for (const [start, base] of baseByStart) {
    if (!matched.has(start)) {
      out.push({ start, end: base.end, kind: 'deleted', previous: base });
    }
  }

  return out.sort((a, z) => a.start.localeCompare(z.start) || a.end.localeCompare(z.end));
}

/** True if a diff result contains at least one real change (add/update/delete). */
export function hasChanges(diff: ReadonlyArray<DiffSlot>): boolean {
  return diff.some((d) => d.kind !== 'unchanged');
}

/**
 * Map a persisted availability row's `pending_action` (the change awaiting PM
 * approval) to a highlight kind. Cleared (null) once approved → no highlight.
 */
export function pendingActionToChangeKind(
  pendingAction: string | null | undefined,
): SlotChangeKind {
  switch (pendingAction) {
    case 'delete':
      return 'deleted';
    case 'update':
      return 'updated';
    case 'add':
      return 'added';
    default:
      return 'unchanged';
  }
}

/**
 * Map a persisted availability row to a change kind for cross-session highlight.
 * A pending_action wins (it names the exact change); otherwise a still-unapproved
 * row (status 'pending_approval') reads as Added, and an approved row is neutral.
 */
export function rowToChangeKind(row: {
  status?: string | null;
  pending_action?: string | null;
}): SlotChangeKind {
  const byAction = pendingActionToChangeKind(row?.pending_action);
  if (byAction !== 'unchanged') return byAction;
  return row?.status === 'pending_approval' ? 'added' : 'unchanged';
}

/** Persisted status + pending_action a slot should carry after a save. */
export interface PersistedSlotState {
  status: 'approved' | 'pending_approval';
  pending_action: 'add' | 'update' | 'delete' | null;
}

/**
 * Translate a diff kind into the (status, pending_action) a save must persist so
 * the Added/Updated/Deleted highlight survives reload until a PM approves (OD-980,
 * AC5). A PM/approver saves changes as already-approved (no highlight); a moderator
 * saves them as pending, tagged with the exact action.
 *
 * `unchanged` returns null so callers keep whatever the slot already carried
 * (an approved slot stays approved; a still-pending slot keeps its own flag).
 */
export function changeKindToPersistedState(
  kind: SlotChangeKind,
  isApprover: boolean,
): PersistedSlotState | null {
  if (isApprover) {
    // Approver edits take effect immediately — never highlighted, never pending.
    return kind === 'unchanged' ? null : { status: 'approved', pending_action: null };
  }
  switch (kind) {
    case 'added':
      return { status: 'pending_approval', pending_action: 'add' };
    case 'updated':
      return { status: 'pending_approval', pending_action: 'update' };
    case 'deleted':
      // Approved row stays bookable; flagged for removal until the PM approves.
      return { status: 'approved', pending_action: 'delete' };
    default:
      return null;
  }
}

export const __TESTING__ = { normalizeHm, toKey };
